import { ChangeEvent, ReactNode, RefObject, createRef } from "react";
import { ViewItem } from "../../../Components/Views/ViewItem";
import { MailHtmlFrame, renderSignaturePreview, sanitizeEmailHtml } from "../../../Components/Views/MailHtmlFrame";
import { controllers } from "../../../Controllers/Controllers";
import { ThemeName } from "../../../Controllers/ThemeController";
import { EmailSettingsFormFields, emptyEmailSettingsFields } from "../../../DataTypes/mail";
import { getRoundedLocalDateTimeString } from "../../../Helpers/Helpers";
import '../Outreach/outreach-styles.css'
import '../Outreach/Categories/categories-styles.css'
import '../Outreach/Mail/mail-styles.css'
import './settings-styles.css'

type SettingsPageState = {
    theme: ThemeName;
    emailFields: EmailSettingsFormFields;
    hasStoredPassword: boolean;
    lastSyncedAt: string | null;
    lastSyncError: string | null;
    emailLoading: boolean;
    emailLoadError: string | null;
    emailSaving: boolean;
    emailSaveMessage: string | null;
    emailSaveError: string | null;
    testingConnection: boolean;
    testResult: { success: boolean; message: string } | null;

    signatureTemplateHtml: string;
    signatureLoading: boolean;
    signatureLoadError: string | null;
    templateSaving: boolean;
    templateSaveMessage: string | null;
    templateSaveError: string | null;

    telegramChatId: string;
    telegramSaving: boolean;
    telegramSaveMessage: string | null;
    telegramSaveError: string | null;
}

const THEME_OPTIONS: { value: ThemeName; label: string; description: string }[] = [
    { value: 'dark', label: 'Dark', description: 'Default. Easier on the eyes in low light.' },
    { value: 'light', label: 'Light', description: 'Bright background, dark text.' },
]


class SettingsPage extends ViewItem {
    state: SettingsPageState;
    templateFileInputRef: RefObject<HTMLInputElement | null> = createRef()

    constructor(props: any) {
        super(props)
        this.state = {
            theme: controllers().theme.theme,
            emailFields: { ...emptyEmailSettingsFields },
            hasStoredPassword: false,
            lastSyncedAt: null,
            lastSyncError: null,
            emailLoading: true,
            emailLoadError: null,
            emailSaving: false,
            emailSaveMessage: null,
            emailSaveError: null,
            testingConnection: false,
            testResult: null,

            signatureTemplateHtml: '',
            signatureLoading: true,
            signatureLoadError: null,
            templateSaving: false,
            templateSaveMessage: null,
            templateSaveError: null,

            telegramChatId: '',
            telegramSaving: false,
            telegramSaveMessage: null,
            telegramSaveError: null,
        }
    }

    componentDidMount(): void {
        this.loadEmailSettings()
        this.loadSignatureTemplate()
    }

    selectTheme = (theme: ThemeName) => {
        controllers().theme.setTheme(theme)
        this.setState({ theme })
    }

    loadEmailSettings = async () => {
        this.setState({ emailLoading: true, emailLoadError: null })
        if (!controllers().auth.userToken) {
            this.setState({ emailLoading: false })
            return
        }
        const response = await controllers().mail.getSettings()
        if (!response || response.statusCode !== 200) {
            this.setState({ emailLoading: false, emailLoadError: response?.error || 'Failed to load email settings.' })
            return
        }
        const account = response.data
        if (!account) {
            this.setState({ emailLoading: false })
            return
        }
        this.setState({
            emailLoading: false,
            hasStoredPassword: !!account.has_password,
            lastSyncedAt: account.last_synced_at || null,
            lastSyncError: account.last_sync_error || null,
            emailFields: {
                email_address: account.email_address || '',
                imap_username: account.imap_username || '',
                imap_host: account.imap_host || '',
                imap_port: account.imap_port ? String(account.imap_port) : '',
                imap_use_ssl: account.imap_use_ssl !== false,
                imap_password: '',
                display_name: account.display_name || '',
                position: account.position || '',
                phone: account.phone || '',
                is_active: account.is_active !== false,
            },
        })
    }

    setEmailField = (field: keyof EmailSettingsFormFields, value: string | boolean) => {
        this.setState((prev: SettingsPageState) => ({
            emailFields: { ...prev.emailFields, [field]: value },
            emailSaveMessage: null,
            emailSaveError: null,
        }))
    }

    handleTextChange = (field: keyof EmailSettingsFormFields) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.setEmailField(field, e.target.value)
    }

    handleSaveEmailSettings = async () => {
        const { emailFields } = this.state
        if (!emailFields.email_address.trim()) {
            this.setState({ emailSaveError: 'Email address is required.' })
            return
        }

        this.setState({ emailSaving: true, emailSaveError: null, emailSaveMessage: null })

        const payload: any = {
            email_address: emailFields.email_address,
            imap_username: emailFields.imap_username,
            imap_host: emailFields.imap_host,
            imap_port: emailFields.imap_port ? Number(emailFields.imap_port) : null,
            imap_use_ssl: emailFields.imap_use_ssl,
            display_name: emailFields.display_name,
            position: emailFields.position,
            phone: emailFields.phone,
            is_active: emailFields.is_active,
        }
        if (emailFields.imap_password) {
            payload.imap_password = emailFields.imap_password
        }

        const response = await controllers().mail.updateSettings(payload)
        if (!response || response.statusCode !== 200) {
            this.setState({ emailSaving: false, emailSaveError: response?.error || 'Failed to save email settings.' })
            return
        }

        this.setState({
            emailSaving: false,
            emailSaveMessage: 'Email settings saved.',
            hasStoredPassword: response.data?.has_password ?? this.state.hasStoredPassword,
        })
        this.setEmailField('imap_password', '')
    }

    handleTestConnection = async () => {
        this.setState({ testingConnection: true, testResult: null })
        const response = await controllers().mail.testConnection()
        this.setState({
            testingConnection: false,
            testResult: {
                success: !!response && response.statusCode === 200,
                message: response?.message || response?.error || 'Test failed.',
            },
        })
    }

    loadSignatureTemplate = async () => {
        this.setState({ signatureLoading: true, signatureLoadError: null })
        if (!controllers().auth.userToken) {
            this.setState({ signatureLoading: false })
            return
        }
        const response = await controllers().mail.getSharedSignature()
        if (!response || response.statusCode !== 200) {
            this.setState({ signatureLoading: false, signatureLoadError: response?.error || 'Failed to load the signature template.' })
            return
        }
        this.setState({ signatureLoading: false, signatureTemplateHtml: response.data?.html || '' })
    }

    handleTemplateChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ signatureTemplateHtml: e.target.value, templateSaveMessage: null, templateSaveError: null })
    }

    handleUploadTemplateClick = () => {
        this.templateFileInputRef.current?.click()
    }

    handleTemplateFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        e.target.value = '' // allow re-selecting the same file later
        if (!file) return

        const reader = new FileReader()
        reader.onload = () => {
            const content = typeof reader.result === 'string' ? reader.result : ''
            this.setState({ signatureTemplateHtml: content, templateSaveMessage: null, templateSaveError: null })
        }
        reader.readAsText(file)
    }

    handleSaveTemplate = async () => {
        this.setState({ templateSaving: true, templateSaveError: null, templateSaveMessage: null })
        const response = await controllers().mail.updateSharedSignature(this.state.signatureTemplateHtml)
        if (!response || response.statusCode !== 200) {
            this.setState({ templateSaving: false, templateSaveError: response?.error || 'Failed to save the signature template.' })
            return
        }
        this.setState({ templateSaving: false, templateSaveMessage: 'Signature template saved.' })
    }

    handleTelegramChange = (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({ telegramChatId: e.target.value, telegramSaveMessage: null, telegramSaveError: null })
    }

    handleSaveTelegram = async () => {
        const { telegramChatId } = this.state
        if (!telegramChatId.trim()) {
            this.setState({ telegramSaveError: 'Chat ID is required.' })
            return
        }
        this.setState({ telegramSaving: true, telegramSaveError: null, telegramSaveMessage: null })
        const response = await controllers().report.saveTelegramChatId(telegramChatId.trim())
        if (!response || response.statusCode !== 200) {
            this.setState({ telegramSaving: false, telegramSaveError: response?.error || 'Failed to save Telegram chat ID.' })
            return
        }
        this.setState({ telegramSaving: false, telegramSaveMessage: 'Telegram chat ID saved.' })
    }

    render(): ReactNode {
        const {
            theme, emailFields, hasStoredPassword, lastSyncedAt, lastSyncError,
            emailLoading, emailLoadError, emailSaving, emailSaveMessage, emailSaveError,
            testingConnection, testResult,
            signatureTemplateHtml, signatureLoading, signatureLoadError,
            templateSaving, templateSaveMessage, templateSaveError,
            telegramChatId, telegramSaving, telegramSaveMessage, telegramSaveError,
        } = this.state

        return (
            <div className="settings-page">
                <div className="settings-header">
                    <h1 className="settings-title">Settings</h1>
                    <p className="settings-subtitle">Manage how the app looks and behaves.</p>
                </div>

                <div className="settings-section">
                    <h3 className="settings-section-title">Appearance</h3>
                    <p className="settings-section-desc">Choose how the interface looks across the whole app.</p>

                    <div className="settings-theme-options">
                        {THEME_OPTIONS.map(option => (
                            <button
                                key={option.value}
                                className={`settings-theme-card ${theme === option.value ? 'settings-theme-card-active' : ''}`}
                                onClick={() => this.selectTheme(option.value)}
                            >
                                <div className={`settings-theme-preview settings-theme-preview-${option.value}`}>
                                    <span className="settings-theme-preview-bar" />
                                    <span className="settings-theme-preview-line" />
                                    <span className="settings-theme-preview-line settings-theme-preview-line-short" />
                                </div>
                                <div className="settings-theme-info">
                                    <span className="settings-theme-label">{option.label}</span>
                                    <span className="settings-theme-desc">{option.description}</span>
                                </div>
                                {theme === option.value && (
                                    <span className="settings-theme-check">✓</span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="settings-section settings-section-spaced">
                    <h3 className="settings-section-title">Email</h3>
                    <p className="settings-section-desc">
                        Connect your own mailbox to send and receive outreach emails from the Inbox tab.
                    </p>

                    {!emailLoading && !emailLoadError && (lastSyncedAt || lastSyncError) && (
                        <p className={`settings-sync-status ${lastSyncError ? 'settings-sync-status-error' : ''}`}>
                            {lastSyncError
                                ? `Last sync failed: ${lastSyncError}`
                                : `Last synced: ${getRoundedLocalDateTimeString(lastSyncedAt)}`}
                        </p>
                    )}

                    {emailLoading && (
                        <div className="outreach-state">
                            <div className="outreach-spinner" />
                        </div>
                    )}

                    {!emailLoading && emailLoadError && (
                        <div className="outreach-banner outreach-banner-error">
                            <span className="outreach-banner-text">{emailLoadError}</span>
                            <button className="outreach-banner-action" onClick={this.loadEmailSettings}>
                                Retry
                            </button>
                        </div>
                    )}

                    {!emailLoading && !emailLoadError && (
                        <>
                            {emailSaveError && <div className="category-form-error">{emailSaveError}</div>}
                            {emailSaveMessage && (
                                <div className="outreach-banner outreach-banner-info settings-email-success">
                                    <span className="outreach-banner-text">{emailSaveMessage}</span>
                                </div>
                            )}
                            {testResult && (
                                <div className={`outreach-banner ${testResult.success ? 'outreach-banner-info' : 'outreach-banner-error'}`}>
                                    <span className="outreach-banner-text">{testResult.message}</span>
                                </div>
                            )}

                            <div className="settings-email-grid">
                                <div className="category-form-group">
                                    <label className="category-form-label">Email Address</label>
                                    <input
                                        className="category-form-input"
                                        type="email"
                                        value={emailFields.email_address}
                                        onChange={this.handleTextChange('email_address')}
                                        placeholder="you@company.com"
                                    />
                                </div>
                                <div className="category-form-group">
                                    <label className="category-form-label">IMAP Username</label>
                                    <input
                                        className="category-form-input"
                                        value={emailFields.imap_username}
                                        onChange={this.handleTextChange('imap_username')}
                                        placeholder="Usually the same as your email address"
                                    />
                                </div>
                                <div className="category-form-group">
                                    <label className="category-form-label">IMAP Host</label>
                                    <input
                                        className="category-form-input"
                                        value={emailFields.imap_host}
                                        onChange={this.handleTextChange('imap_host')}
                                        placeholder="imap.gmail.com"
                                    />
                                </div>
                                <div className="category-form-group">
                                    <label className="category-form-label">IMAP Port</label>
                                    <input
                                        className="category-form-input"
                                        value={emailFields.imap_port}
                                        onChange={this.handleTextChange('imap_port')}
                                        placeholder="993"
                                    />
                                </div>
                                <div className="category-form-group">
                                    <label className="category-form-label">Password</label>
                                    <input
                                        className="category-form-input"
                                        type="password"
                                        value={emailFields.imap_password}
                                        onChange={this.handleTextChange('imap_password')}
                                        placeholder={hasStoredPassword ? 'Leave blank to keep current password' : 'Enter password'}
                                    />
                                </div>
                            </div>

                            <label className="category-form-checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={emailFields.imap_use_ssl}
                                    onChange={(e) => this.setEmailField('imap_use_ssl', e.target.checked)}
                                />
                                Use SSL
                            </label>

                            <label className="category-form-checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={emailFields.is_active}
                                    onChange={(e) => this.setEmailField('is_active', e.target.checked)}
                                />
                                Active (enables sending/syncing)
                            </label>

                            <div className="settings-email-grid">
                                <div className="category-form-group">
                                    <label className="category-form-label">Display Name</label>
                                    <input
                                        className="category-form-input"
                                        value={emailFields.display_name}
                                        onChange={this.handleTextChange('display_name')}
                                        placeholder="Defaults to your account name"
                                    />
                                </div>
                                <div className="category-form-group">
                                    <label className="category-form-label">Position</label>
                                    <input
                                        className="category-form-input"
                                        value={emailFields.position}
                                        onChange={this.handleTextChange('position')}
                                        placeholder="e.g. Founder, Sales Manager"
                                    />
                                </div>
                                <div className="category-form-group">
                                    <label className="category-form-label">Phone</label>
                                    <input
                                        className="category-form-input"
                                        value={emailFields.phone}
                                        onChange={this.handleTextChange('phone')}
                                        placeholder="Optional"
                                    />
                                </div>
                            </div>
                            <p className="settings-section-desc settings-signature-hint">
                                These fill in your signature automatically — see below.
                            </p>

                            <div className="settings-email-actions">
                                <button
                                    className="outreach-edit-btn"
                                    onClick={this.handleTestConnection}
                                    disabled={testingConnection || emailSaving}
                                >
                                    {testingConnection ? 'Testing...' : 'Test Connection'}
                                </button>
                                <button
                                    className="outreach-add-btn"
                                    onClick={this.handleSaveEmailSettings}
                                    disabled={emailSaving}
                                >
                                    {emailSaving ? 'Saving...' : 'Save Email Settings'}
                                </button>
                            </div>
                        </>
                    )}
                </div>

                <div className="settings-section settings-section-spaced">
                    <h3 className="settings-section-title">Email Signature</h3>
                    <p className="settings-section-desc">
                        One shared template is appended to every outgoing email — your Display Name, Position, and
                        Phone above get filled in automatically.
                        {!controllers().auth.isAdmin && ' Editing the template itself is admin-only.'}
                    </p>

                    {signatureLoading && (
                        <div className="outreach-state">
                            <div className="outreach-spinner" />
                        </div>
                    )}

                    {!signatureLoading && signatureLoadError && (
                        <div className="outreach-banner outreach-banner-error">
                            <span className="outreach-banner-text">{signatureLoadError}</span>
                            <button className="outreach-banner-action" onClick={this.loadSignatureTemplate}>
                                Retry
                            </button>
                        </div>
                    )}

                    {!signatureLoading && !signatureLoadError && (
                        <>
                            {signatureTemplateHtml ? (
                                <div className="settings-signature-preview">
                                    <span className="settings-signature-preview-label">Your Signature Preview</span>
                                    <MailHtmlFrame html={sanitizeEmailHtml(renderSignaturePreview(signatureTemplateHtml, emailFields))} />
                                </div>
                            ) : (
                                <p className="settings-section-desc">No signature template has been set up yet.</p>
                            )}

                            {controllers().auth.isAdmin && (
                                <>
                                    {templateSaveError && <div className="category-form-error settings-template-error">{templateSaveError}</div>}
                                    {templateSaveMessage && (
                                        <div className="outreach-banner outreach-banner-info settings-email-success">
                                            <span className="outreach-banner-text">{templateSaveMessage}</span>
                                        </div>
                                    )}

                                    <div className="category-form-group settings-template-editor">
                                        <div className="settings-signature-label-row">
                                            <label className="category-form-label">Template HTML</label>
                                            <button type="button" className="settings-signature-upload-btn" onClick={this.handleUploadTemplateClick}>
                                                Upload HTML file...
                                            </button>
                                            <input
                                                ref={this.templateFileInputRef}
                                                type="file"
                                                accept=".html,.htm,text/html"
                                                className="settings-signature-file-input"
                                                onChange={this.handleTemplateFileChange}
                                            />
                                        </div>
                                        <textarea
                                            className="category-form-textarea"
                                            value={signatureTemplateHtml}
                                            onChange={this.handleTemplateChange}
                                            rows={8}
                                            placeholder="Placeholders: {{lead_manager_name}}, {{lead_manager_position}}, {{lead_manager_email}}, {{lead_manager_phone}}, {{unsubscribe_url}}"
                                        />
                                    </div>

                                    <div className="settings-email-actions">
                                        <button
                                            className="outreach-add-btn"
                                            onClick={this.handleSaveTemplate}
                                            disabled={templateSaving}
                                        >
                                            {templateSaving ? 'Saving...' : 'Save Template'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>

                <div className="settings-section settings-section-spaced">
                    <h3 className="settings-section-title">Telegram Reports</h3>
                    <p className="settings-section-desc">
                        Where lead status reports get sent from the Report tab. Find your chat ID from your Telegram bot.
                    </p>

                    {telegramSaveError && <div className="category-form-error">{telegramSaveError}</div>}
                    {telegramSaveMessage && (
                        <div className="outreach-banner outreach-banner-info settings-email-success">
                            <span className="outreach-banner-text">{telegramSaveMessage}</span>
                        </div>
                    )}

                    <div className="category-form-group">
                        <label className="category-form-label">Telegram Chat ID</label>
                        <input
                            className="category-form-input"
                            value={telegramChatId}
                            onChange={this.handleTelegramChange}
                            placeholder="123456789"
                        />
                    </div>

                    <div className="settings-email-actions">
                        <button
                            className="outreach-add-btn"
                            onClick={this.handleSaveTelegram}
                            disabled={telegramSaving}
                        >
                            {telegramSaving ? 'Saving...' : 'Save Chat ID'}
                        </button>
                    </div>
                </div>
            </div>
        )
    }
}

export { SettingsPage }
